import { OwnerId, OwnerName } from "../../state/authSelectors";
import { TopBarControl } from "../StyledControl";
import { notify, clientConfig, getSdk, text } from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { Authorization } from "../../state/authAtoms";
import { GraphQLClient } from "graphql-request";
import { IsInPortrait } from "../../state/selectors";
import { MousedOverTextHelper } from "../../state/uiAtoms";
import { PublishAtom } from "../../state/publish";
import { StackCfKeyAtom } from "../../state/stackAtoms";
import { StackFileNameSelectorFamily } from "../../state/stackSelectors";
import { useState } from "react";
import { ShowTknsPanelAtom } from "../../state/tknsAtoms";
import { useMetrics } from "../../hooks/useMetrics";
import { utilityFileNameMessage } from "../../lib/codeMessages";
import { isUpgradeCode, returnCodeMessage } from "../../lib/returnCodeMessage";
import { InlineSunSpinner } from "../InlineSunSpinner";

function PublishControl() {
	const setTextHelper = useSetRecoilState(MousedOverTextHelper);
	const isInPortrait = useRecoilValue(IsInPortrait);
	const [publishing, setPublishing] = useState(false);
	const [published, setPublished] = useRecoilState(PublishAtom);
	const authorization = useRecoilValue(Authorization);
	const ownerName = useRecoilValue(OwnerName);
	const ownerId = useRecoilValue(OwnerId);
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const stackFileName = useRecoilValue(StackFileNameSelectorFamily(stackCfKey));
	const { track } = useMetrics();
	const setShowTknsPanel = useSetRecoilState(ShowTknsPanelAtom);
	const handleClick = async () => {
		try {
			if (authorization.isLoggedIn && ownerName && ownerId) {
				// set the code to any error messages based on the stackFileName
				if (stackFileName in utilityFileNameMessage) {
					const key = stackFileName as keyof typeof utilityFileNameMessage;
					notify(utilityFileNameMessage[key]);
					return;
				}

				if (!stackCfKey) return;

				const client = new GraphQLClient(clientConfig.gqlWriteStack());
				const sdk = getSdk(client);
				const user = getAuth().currentUser;
				if (!user) return;

				const jwt = await getIdToken(user);

				if (!published) {
					setPublishing(true);
					const res = await sdk.PublishStack({ stackCfKey, jwt });
					if (!res.publishStack?.success) {
						setPublishing(false);
						// Numeric return code → friendly message; a free-tier wall
						// (the conversion moment) also opens the Plus card.
						const code = res.publishStack?.message;
						console.error(res);
						notify(returnCodeMessage(code));
						if (isUpgradeCode(code)) setShowTknsPanel(true);
						return;
					}
					track('story_published', { stackCfKey, stackFileName });
				}

				if (published) {
					setPublishing(true);
					const res = await sdk.UnPublishStack({ stackCfKey, jwt });
					if (!res.unPublishStack?.success) {
						setPublishing(false);
						notify(text.errors.publishFailed);
						console.error(res);
						return;
					}
				}

				setPublished(!published);
				setPublishing(false);
			}
		} catch (error) {
			console.error("Error in handleClick:", error);
		}
	};

	const mouseOver = () => {
		setTextHelper(text.controls.publish);
	};

	const mouseLeave = () => {
		setTextHelper(undefined);
	};

	return (
		<TopBarControl
			$isInPortrait={isInPortrait}
			onClick={handleClick}
			onMouseOver={mouseOver}
			onMouseLeave={mouseLeave}
			id="action-control-publish"
			data-testid="publish-control"
			aria-label={published ? text.controls.unpublish : text.controls.publish}
			aria-busy={publishing}
		>
			{publishing ? (
				// Keep the button's footprint: reserve the label's width (hidden)
				// and overlay the spinning sun, so the control doesn't collapse.
				<span style={{ position: "relative", display: "inline-block" }}>
					<span aria-hidden="true" style={{ visibility: "hidden" }}>
						{published ? text.controls.unpublish : text.controls.publish}
					</span>
					<span
						style={{
							position: "absolute",
							inset: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<InlineSunSpinner />
					</span>
				</span>
			) : published ? (
				text.controls.unpublish
			) : (
				text.controls.publish
			)}
		</TopBarControl>
	);
}

export default PublishControl;
